import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type {
  DataEntry,
  DataEntryListResult,
  DataEntryTagFilterInput,
  DataEntryTagSearchResult,
} from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';

export interface UseDatasetEntriesParams {
  dataSetId: string;
  dataSetVersion: number;
  filters?: DataEntryTagFilterInput[];
  /** 0 = AND, 1 = OR. Only used when `filters` is non-empty. */
  matchMode?: 0 | 1;
}

export interface UseDatasetEntriesResult {
  entries: DataEntry[];
  totalCount: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

const PAGE_SIZE = 20;

function dedup(existing: DataEntry[], incoming: DataEntry[]): DataEntry[] {
  const seen = new Set(existing.map((e) => e.id));
  return incoming.filter((e) => !seen.has(e.id));
}

export function useDatasetEntries(
  params: UseDatasetEntriesParams,
): UseDatasetEntriesResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);

  const [entries, setEntries] = useState<DataEntry[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [continuationToken, setContinuationToken] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  const prefetchedRef = useRef<
    DataEntryListResult | DataEntryTagSearchResult | null
  >(null);
  const prefetchTokenRef = useRef<string | null>(null);

  const refresh = useCallback(() => {
    setRefreshCount((n) => n + 1);
  }, []);

  const activeFilters = params.filters ?? [];
  const matchMode: 0 | 1 = params.matchMode ?? 0;
  const hasFilters = activeFilters.length > 0;

  const filtersKey = JSON.stringify(activeFilters);

  const paramsRef = useRef({
    service,
    dataSetId: params.dataSetId,
    dataSetVersion: params.dataSetVersion,
    activeFilters,
    matchMode,
    hasFilters,
  });
  paramsRef.current = {
    service,
    dataSetId: params.dataSetId,
    dataSetVersion: params.dataSetVersion,
    activeFilters,
    matchMode,
    hasFilters,
  };

  function fetchPage(
    token?: string,
    includeCount?: boolean,
  ): Promise<DataEntryListResult | DataEntryTagSearchResult> {
    const p = paramsRef.current;
    if (p.hasFilters) {
      return p.service.searchDataEntriesByTags({
        dataSetId: p.dataSetId,
        filters: p.activeFilters,
        matchMode: p.matchMode,
        pageSize: PAGE_SIZE,
        continuationToken: token,
      });
    }
    return p.service.listDataEntries({
      dataSetId: p.dataSetId,
      dataSetVersion: p.dataSetVersion,
      pageSize: PAGE_SIZE,
      continuationToken: token,
      includeCount: includeCount ?? false,
    });
  }

  // Reset and fetch page 1 when dataset, version, filters, or refresh changes.
  useEffect(() => {
    let cancelled = false;

    prefetchedRef.current = null;
    prefetchTokenRef.current = null;

    setIsLoading(true);
    setError(null);
    setEntries([]);
    setTotalCount(null);
    setContinuationToken(null);

    fetchPage(undefined, true)
      .then((result) => {
        if (cancelled) return;
        setEntries(result.items);
        setTotalCount('totalCount' in result ? result.totalCount : null);
        setContinuationToken(result.continuationToken);
        setIsLoading(false);

        if (result.continuationToken) {
          prefetchTokenRef.current = result.continuationToken;
          fetchPage(result.continuationToken)
            .then((prefetched) => {
              if (
                cancelled ||
                prefetchTokenRef.current !== result.continuationToken
              ) {
                return;
              }
              prefetchedRef.current = prefetched;
            })
            .catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load entries');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    service,
    params.dataSetId,
    params.dataSetVersion,
    filtersKey,
    matchMode,
    refreshCount,
  ]);

  const loadMore = useCallback(() => {
    if (!continuationToken || isLoadingMore) return;

    if (
      prefetchedRef.current &&
      prefetchTokenRef.current === continuationToken
    ) {
      const prefetched = prefetchedRef.current;
      prefetchedRef.current = null;
      prefetchTokenRef.current = null;

      setEntries((prev) => [...prev, ...dedup(prev, prefetched.items)]);
      setContinuationToken(prefetched.continuationToken);

      if (prefetched.continuationToken) {
        prefetchTokenRef.current = prefetched.continuationToken;
        fetchPage(prefetched.continuationToken)
          .then((next) => {
            if (prefetchTokenRef.current !== prefetched.continuationToken) {
              return;
            }
            prefetchedRef.current = next;
          })
          .catch(() => undefined);
      }
      return;
    }

    setIsLoadingMore(true);
    fetchPage(continuationToken)
      .then((result) => {
        setEntries((prev) => [...prev, ...dedup(prev, result.items)]);
        setContinuationToken(result.continuationToken);
        setIsLoadingMore(false);

        if (result.continuationToken) {
          prefetchTokenRef.current = result.continuationToken;
          fetchPage(result.continuationToken)
            .then((next) => {
              if (prefetchTokenRef.current !== result.continuationToken) return;
              prefetchedRef.current = next;
            })
            .catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : 'Failed to load more entries',
        );
        setIsLoadingMore(false);
      });
  }, [continuationToken, isLoadingMore]);

  return {
    entries,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    hasMore: continuationToken !== null,
    loadMore,
    refetch: refresh,
  };
}
